import { useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { Creators as DataCreators } from "model/data"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { warn } from "helpers/dev-logger"
import { createUuid } from "helpers/types/uuid"
import { useAppDispatch, useAppStore } from "hooks/redux"
import type { ReduxState } from "model/state"
import {
  useCreateInstance,
  useSaveInstanceWorkspace,
  DASHBOARD_INSTANCES_RESOURCE,
} from "resources/dashboard-instances"
import { useCreateLayout, useLayouts, LAYOUTS_RESOURCE, type Layout } from "resources/layouts"
import { instantiateInstanceFromLayout, buildDefaultDashboardConfig } from "../layouts/instantiate-instance-from-layout"
import { useHasThematicBetasContentSet } from "./use-has-thematic-betas-content-set"

export interface CreateDashboard {
  // Resolves on success (toasts + navigates); rejects on failure so the caller can keep its dialog
  // open with an inline retry message instead of silently closing on a swallowed error. [APP-3309]
  // Copies a chosen layout's saved config into a new user layout (superuser template-picker path).
  createDashboard: (name: string, sourceLayout: Layout) => Promise<void>
  // Create from the user's own layout: reuse it as a new instance (no layout minted), or synthesize
  // one from client defaults if they have none. [APP-3284]
  createDefaultDashboard: (name: string) => Promise<void>
  // False until content-set entitlements are known — createDefaultDashboard's synthesize path reads
  // hasThematicBetasContentSet, which defaults false while loading; calling it before this is true
  // can permanently persist a layout missing the panel for an entitled account. [APP-3309]
  isContentSetsLoaded: boolean
}

// `layoutsEnabled` (default true) forwards to useLayouts so a caller mounted ahead of need — e.g. a
// dialog that's rendered but not yet open — can defer the createDefaultDashboard reuse-check fetch
// until it's actually needed. [APP-3309]
export function useCreateDashboard(layoutsEnabled = true): CreateDashboard {
  const store = useAppStore()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [layouts] = useLayouts(layoutsEnabled)
  const createLayout = useCreateLayout()
  const createInstance = useCreateInstance()
  const saveWorkspace = useSaveInstanceWorkspace()
  // Same entitlement source useSyncPanelsPreferences uses for a migrated dashboard's default panels,
  // so a synthesized default dashboard (below) matches one produced by migration. [APP-3284]
  const { hasThematicBetasContentSet, isContentSetsLoaded } = useHasThematicBetasContentSet()

  // Capture the account BEFORE the await: reading it after would target the wrong account if the user
  // switched mid-flight (created under the old, navigate under the new → 404). [APP-3266]
  const getAccountAlias = useCallback(
    () => (store.getState() as unknown as ReduxState).global.selected.account,
    [store],
  )

  const finishCreate = useCallback(
    (instanceId: string, accountAlias: string | null, resetCollections: string[]) => {
      // Reset the instances list (so the resolver refetches with the new id before we route), plus the
      // layouts list when this also minted a layout. [APP-3271]
      dispatch(DataCreators.resetCollections(resetCollections))
      // Route by instanceId (a UUID), NOT the server-generated slug: the resolver's isUnmigratedSlug
      // check treats an unmatched non-UUID route id as a legacy dashboard to lazy-migrate. If this
      // navigate lands before the reset above refetches, a slug route would misfire that migration
      // and mint a duplicate instance. A UUID route id falls into the (harmless) unknown-id redirect
      // instead — it still matches once the refetch lands. [APP-3266]
      if (accountAlias) navigate(`/${accountAlias}/dashboard/${instanceId}`)
      dispatch(NotificationCreators.add("success", null, "Created a new dashboard."))
    },
    [dispatch, navigate],
  )

  // Logs then rethrows so the caller's own catch can keep its dialog open with an inline retry
  // message — a toast here would fire even though the dialog is about to show the same failure. [APP-3309]
  const reportFailure = useCallback((error: unknown): never => {
    warn("New dashboard creation failed", error)
    throw error
  }, [])

  const createDashboard = useCallback(
    async (name: string, sourceLayout: Layout) => {
      const accountAlias = getAccountAlias()
      try {
        const { instanceId } = await instantiateInstanceFromLayout({
          name,
          config: sourceLayout.config,
          createLayout,
          createInstance,
          saveWorkspace,
        })
        finishCreate(instanceId, accountAlias, [DASHBOARD_INSTANCES_RESOURCE, LAYOUTS_RESOURCE])
      } catch (error: unknown) {
        reportFailure(error)
      }
    },
    [getAccountAlias, createLayout, createInstance, saveWorkspace, finishCreate, reportFailure],
  )

  const createDefaultDashboard = useCallback(
    async (name: string) => {
      const accountAlias = getAccountAlias()
      try {
        const existingLayout = (layouts ?? []).find((layout) => layout.ownerType === "user")
        if (existingLayout) {
          // Reuse: a new dashboard is just another instance of the user's one layout — only instances change.
          const instanceId = createUuid()
          await createInstance({ instanceId, name, layoutId: existingLayout.id })
          finishCreate(instanceId, accountAlias, [DASHBOARD_INSTANCES_RESOURCE])
        } else {
          // No layout yet: synthesize one from client defaults, then instance from it (both lists change).
          const { config, workspace } = buildDefaultDashboardConfig(hasThematicBetasContentSet)
          const { instanceId } = await instantiateInstanceFromLayout({
            name,
            config,
            workspace,
            createLayout,
            createInstance,
            saveWorkspace,
          })
          finishCreate(instanceId, accountAlias, [DASHBOARD_INSTANCES_RESOURCE, LAYOUTS_RESOURCE])
        }
      } catch (error: unknown) {
        reportFailure(error)
      }
    },
    [getAccountAlias, layouts, createInstance, createLayout, saveWorkspace, hasThematicBetasContentSet, finishCreate, reportFailure],
  )

  return { createDashboard, createDefaultDashboard, isContentSetsLoaded }
}
